'use client';

import { useState } from 'react';
import { 
  FaCheck, 
  FaTimes, 
  FaEye, 
  FaEdit, 
  FaTrash,
  FaExclamationTriangle
} from 'react-icons/fa';
import { motion } from 'framer-motion';

// Define booking type
export interface Booking {
  id: string;
  guestName: string;
  email: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  guests: {
    adults: number;
    children: number;
  };
  status: 'pending' | 'confirmed' | 'canceled' | 'completed';
  paymentStatus: 'paid' | 'partial' | 'pending' | 'refunded';
  totalAmount: number;
  paidAmount: number;
  createdAt: string;
}

interface BookingsListProps {
  bookings?: Booking[];
  filter?: 'all' | 'upcoming' | 'past' | 'canceled';
  onViewBooking?: (booking: Booking) => void;
  onEditBooking?: (booking: Booking) => void;
  onConfirmBooking?: (bookingId: string) => void;
  onCancelBooking?: (bookingId: string) => void;
  onDeleteBooking?: (bookingId: string) => void;
  onCompleteBooking?: (bookingId: string) => void;
}

export default function BookingsList({ 
  bookings = [], 
  filter = 'all',
  onViewBooking = () => {},
  onEditBooking = () => {},
  onConfirmBooking = () => {},
  onCancelBooking = () => {},
  onDeleteBooking = () => {},
  onCompleteBooking = () => {}
}: BookingsListProps) {
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // Function to format date in a readable format
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Function to calculate duration of stay
  const calculateStayDuration = (checkIn: string, checkOut: string) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  // Function to get status badge color
  const getStatusBadgeColor = (status: Booking['status']) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Function to get payment status badge color
  const getPaymentBadgeColor = (paymentStatus: Booking['paymentStatus']) => {
    switch (paymentStatus) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'refunded':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Booking ID
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Guest
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dates
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Payment
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bookings.length > 0 ? (
            bookings.map((booking) => (
              <motion.tr 
                key={booking.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {booking.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{booking.guestName}</div>
                  <div className="text-sm text-gray-500">{booking.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatDate(booking.checkIn)} - {formatDate(booking.checkOut)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {calculateStayDuration(booking.checkIn, booking.checkOut)} nights
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(booking.status)}`}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentBadgeColor(booking.paymentStatus)}`}>
                    {booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  RM {booking.totalAmount.toFixed(2)}
                  {booking.paymentStatus === 'partial' && (
                    <div className="text-xs text-gray-500">
                      Paid: RM {booking.paidAmount.toFixed(2)}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <button
                    onClick={() => onViewBooking(booking)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <FaEye className="inline mr-1" /> View
                  </button>
                  <button
                    onClick={() => onEditBooking(booking)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <FaEdit className="inline mr-1" /> Edit
                  </button>
                  {booking.status === 'pending' && (
                    <button
                      onClick={() => onConfirmBooking(booking.id)}
                      className="text-green-600 hover:text-green-900"
                    >
                      <FaCheck className="inline mr-1" /> Confirm
                    </button>
                  )}
                  {(booking.status === 'pending' || booking.status === 'confirmed') && (
                    <button
                      onClick={() => onCancelBooking(booking.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTimes className="inline mr-1" /> Cancel
                    </button>
                  )}
                  {booking.status === 'confirmed' && (
                    <button
                      onClick={() => onCompleteBooking(booking.id)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      <FaCheck className="inline mr-1" /> Complete
                    </button>
                  )}
                </td>
              </motion.tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="px-6 py-12 text-center">
                <FaExclamationTriangle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filter === 'all' 
                    ? 'There are no bookings in the system yet.' 
                    : `There are no ${filter} bookings to display.`}
                </p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
